<script lang="ts">
  import { onMount } from "svelte"
  import { SquarePen, Trash2, File, RefreshCw } from "lucide-svelte"
  import Layout from "$app/Layouts/Admin.svelte"
  import DataTable from "$app/components/DataTable.svelte"
  import Loading from "$app/components/Loading.svelte"
  import { formatDate, xhr, toast } from "$app/utils"
  import Form from "./Form.svelte"
  import Modal from "$app/components/Modal.svelte"

  type UserTypeExt = UserType & {
    plan_count: number
  }

  let props = $props()

  let users: UserTypeExt[] = $state(props.users)
  let plan_count: { [key: number]: number } = props.plan_count || {}
  let limit: number = props.limit
  let sort_field = $state(props.sort_field || "name")
  let sort_direction = $state(props.sort_direction || "asc")

  let userFormStatus = $state("")
  let openFormModal = $state(false)

  onMount(() => {
    users = setUsersExtraData(users, plan_count)
  })

  function setUsersExtraData(
    users: UserTypeExt[],
    extraData: any
  ): UserTypeExt[] {
    return users.map((user) => {
      user.plan_count = extraData[user.id] || 0
      return user
    })
  }

  let loadMore = $state(true)
  let loading = $state(false)
  let page = 1

  async function loadUsers(): Promise<void> {
    if (loadMore === false || loading === true) return
    try {
      loading = true
      page++
      const resp = await xhr.get(`/admin/users.json?page=${page}`, {
        params: {
          q: "1",
          page: page,
          ...searchParams()
        }
      })
      const newUsers = resp.data.users.map((p) => {
        return { ...p, plan_count: resp.data.plan_count[p.id] || 0 }
      })
      users = [...users, ...newUsers]

      loadMore = !(resp.data.users.length < limit)
    } catch (e) {
      console.error(e)
    } finally {
      loading = false
    }
  }

  function searchParams(): object {
    const params: { [key: string]: string } = {}
    if (searchForm.name) params.name = searchForm.name
    if (searchForm.email) params.email = searchForm.email
    if (searchForm.createdAt) {
      params.created_at = searchForm.createdAt
      params.createdAtStart = searchForm.createdAtStart || ""
      params.createdAtEnd = searchForm.createdAtEnd || ""
    }
    params["q"] = searchForm.name || searchForm.createdAt ? "1" : ""
    params.sort_field = sort_field
    params.sort_direction = sort_direction

    return params
  }

  let prevSearch = ""

  async function searchUsers(): Promise<void> {
    page = 1

    try {
      const resp = await xhr.get(`/admin/users.json`, {
        params: searchParams()
      })

      users = setUsersExtraData(resp.data.users, resp.data.plan_count)

      loadMore = !(resp.data.users.length < limit)
      prevSearch = JSON.stringify(searchParams())
    } catch (e) {
      console.error(e)
    }
  }

  let deleting = $state(false)
  let delMsg = $state("")
  let currentDeleteUser: any = $state({})
  async function openDeleteModal(user: UserType) {
    currentDeleteUser = { id: 0 }
    user._selected = true
    users = users
    deleting = true
    const resp = await xhr.get(`/admin/users/${user.id}.json`)

    currentDeleteUser = resp.data
  }

  let disableDelete = $state(false)
  async function deleteUser(): Promise<void> {
    try {
      disableDelete = true
      const resp = await xhr.delete(`/admin/users/${currentDeleteUser.id}.json`)

      toast("User has been deleted", "success")
    } catch (e) {
      console.error(e)
      toast("There was an error deleting the user", "error")
    } finally {
      deleting = false
    }
  }

  async function setSort(sortV: any) {
    if (sort_field === sortV.key) {
      sort_direction = sort_direction === "asc" ? "desc" : "asc"
    } else {
      sort_field = sortV.key
      sort_direction = "asc"
    }

    page = 1
    searchUsers()
  }

  let currentUser: UserTypeExt = $state({}) as UserTypeExt
  function openEdit(user: UserTypeExt): void {
    currentUser = { ...user }
    const loc = window.location.href
    window.location.href = loc.replace(window.location.hash, `#edit/${user.id}`)
    userFormStatus = "edit"
    openFormModal = true
  }

  function viewUser(user: UserTypeExt): void {
    currentUser = { ...user }
    const loc = window.location.href
    window.location.href = `/admin/users/${user.id}`
  }

  let searchForm = $state({
    name: "",
    email: "",
    createdAt: "",
    createdAtStart: "",
    createdAtEnd: ""
  })

  const options = [
    { value: "7.days", label: "Last 7 days" },
    { value: "30.days", label: "Last 30 days" },
    { value: "90.days", label: "Last 90 days" },
    { value: "180.days", label: "Last 180 days" },
    { value: "1.year", label: "Last year" },
    { value: "custom", label: "Custom" }
  ]

  function updateUser(user: UserTypeExt) {
    const idx = users.findIndex((u) => user.id === u.id)
    users[idx] = { ...users[idx], ...user } as UserTypeExt
  }

  function createUser(user: UserTypeExt) {
    users = [...users, user]
  }

  const fields = [
    { key: "id", label: "ID", sorting: true },
    {
      key: "avatar_url",
      label: "Avatar",
      format: function (u) {
        return `<img src="${u.avatar_url}" class="w-10 h-10 rounded-full" />`
      }
    },
    {
      key: "name",
      label: "Name",
      sorting: true,
      format: (u) =>
        `<a class="hover:text-blue-600 hover:underline" href="/admin/plans/${u.id}">${u.name}</a>`
    },
    {
      key: "email",
      label: "Email",
      blankText: "<span class='text-gray-500'>No email</span>",
      sorting: true
    },
    { key: "plan_count", label: "Plans" },

    {
      key: "created_at",
      label: "Created On",
      format: (d) => formatDate(d.created_at),
      sorting: true
    },
    { key: "birthdate", label: "Birthdate" },
    { key: "title", label: "Title" },
    { key: "mobile_number", label: "Mobile Number" }
  ]

  let validSearch = $derived(
    searchForm.name || searchForm.email || searchForm.createdAt
  )
</script>

<Layout {...props}>
  <span slot="title">Users</span>

  <Loading {loading}>
    <div>
      <div class="flex gap-2 py-4">
        <div class="input-cont">
          <label
            class="block text-sm font-medium leading-6 text-gray-900"
            for="">Name</label
          >
          <input
            class="!mt-0 text-gray-600 input"
            type="text"
            bind:value={searchForm.name}
          />
        </div>
        <div class="input-cont">
          <label
            class="block text-sm font-medium leading-6 text-gray-900"
            for="">Email</label
          >
          <input
            class="!mt-0 text-gray-600 input"
            type="text"
            bind:value={searchForm.email}
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium leading-6 text-gray-900"
            for="">Created on</label
          >
          <select
            class="!mt-0 w-48 text-gray-600"
            bind:value={searchForm.createdAt}
          >
            <option value="">None</option>
            {#each options as option}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
        </div>

        <div
          class:hidden={searchForm.createdAt !== "custom"}
          class="flex gap-1"
        >
          <div>
            <label for="">Created on start</label>
            <input
              type="date"
              class="input-admin"
              bind:value={searchForm.createdAtStart}
            />
          </div>
          <div>
            <label for="">Created on end</label>
            <input
              type="date"
              class="input-admin"
              bind:value={searchForm.createdAtEnd}
            />
          </div>
        </div>
        <div class="flex items-end">
          <button
            class="btn primary"
            disabled={!validSearch}
            onclick={searchUsers}
          >
            Search
          </button>
        </div>
      </div>

      <DataTable
        {fields}
        sortField={sort_field}
        sortDirection={sort_direction}
        showNum={true}
        data={users}
        noValue={"<span class='text-gray-500'>No value</span>"}
        onsort={(e) => setSort(e)}
      >
        <svelte:fragment slot="actions" let:item>
          <div class="flex gap-4 px-2">
            <button onclick={() => viewUser(item)}>
              <File class="size-6 text-blue-600 hover:text-blue-500" />
            </button>
            <button onclick={() => openEdit(item)}>
              <SquarePen class="size-6 text-blue-600 hover:text-blue-500" />
            </button>
            <button onclick={() => openDeleteModal(item)}>
              <Trash2 class="size-6 text-red-600 hover:text-red-500" />
            </button>
          </div>
        </svelte:fragment>
      </DataTable>

      {#if loadMore}
        <div class="text-center p-4">
          <button onclick={loadUsers} class="btn primary"> Load More </button>
        </div>
      {/if}
    </div>
  </Loading>

  <Modal bind:open={openFormModal} large={false}>
    <Form
      user={currentUser}
      status={userFormStatus}
      onupdate={(user) => updateUser(user)}
      oncreate={(user) => createUser(user)}
    />
  </Modal>

  <!-- Delete modal-->
  <Modal
    bind:open={deleting}
    onclose={() => {
      deleting = false
      const idx = users.findIndex((p) => p.id === currentDeleteUser.id)
      users[idx]._selected = false
    }}
  >
    <div class="pt-4" style="width:350px; z-index: 100;">
      {#if !currentDeleteUser.id}
        <div class="flex text-gray-500">
          <RefreshCw class="size-12 text-gray-600" />
          <span class="ml-2">Loading...</span>
        </div>
      {:else}
        <h2 class="text-base font-semibold leading-6 text-gray-900">
          Delete User
        </h2>

        <div class="mt-2">
          <span class="font-semibold text-gray-700">{currentDeleteUser.id}</span
          >
          <span class="font-semibold text-gray-700"
            >{currentDeleteUser.name}</span
          >
          is a member of:
          <ul class="list-disc pl-8">
            <li class="text-gray-600">{currentDeleteUser.plan_count} plans</li>
          </ul>
        </div>

        <div class="mt-4 text-gray-700">
          Please type DELETE to delete the user
        </div>

        <div class="input-cont">
          <input
            type="text"
            class="text-gray-700"
            bind:value={delMsg}
            placeholder="Type DELETE"
          />
        </div>

        <button
          class="btn danger w-full justify-items-center mt-2"
          disabled={delMsg !== "DELETE" || disableDelete}
          onclick={deleteUser}
        >
          <span class="font-bold text-lg mx-auto"> DELETE </span>
        </button>
      {/if}
    </div>
  </Modal>
</Layout>
